import Popover from '@mui/material/Popover'
import React, { useContext, useState } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import greenFilledTick from 'src/assets/green_filled_tick.png'
import pencilIcon from 'src/assets/pencil.png'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import {
  getLanguageDrawerItems,
  getPreferencesDrawerItems,
  getProfileDetails,
  getProfilePayload,
} from 'src/consumer/helpers/profilePageHelper'
import { mediaURL } from 'src/consumer/services'
import { postData } from 'src/consumer/services/profileService'
import { GENDER_LIST } from '../../constants'
import { InputField } from './InputField'
import { DisplayFlex } from './ProfileLeftPannelStyle'
import {
  CancelButton,
  ContentLanguagesContainer,
  ContentLanguagesOptionContainer,
  ContentLanguagesOptionExtraText,
  ContentPreferencesContainer,
  ContentPreferencesOptionContainer,
  EditButton,
  EditIcon,
  GenderSelectContainer,
  GenderSelectOptionContainer,
  OptionSeparationLine,
  SaveButton,
  SelectOptionCategoryPicture,
  SelectOptionPicture,
  SpaceBetweenFlex,
  StyledAgeGenderContainer,
  YourProfileActions,
  YourProfileContainer,
  YourProfileContent,
  YourProfileForm,
  YourProfileTitle,
} from './YourProfileStyle'

const YourProfile = (props) => {
  const { localizedContent } = props

  const { profileData, setProfileData } = useContext(ProfileContext)
  const { setupData } = useContext(SetupContext)

  const { name, age, gender, contentPreferences, contentLanguages } = getProfileDetails(profileData)
  const [formDisabled, setFormDisabled] = useState(true)
  const [saveButtonLoading, setSaveButtonLoading] = useState(false)

  const initialState = {
    name: {
      value: name,
      isValid: true,
    },
    age: {
      value: age,
      isValid: true,
    },
    gender: {
      value: gender,
      isValid: true,
    },
    contentPreferences: {
      value: contentPreferences,
      isValid: true,
    },
    contentLanguages: {
      value: contentLanguages,
      isValid: true,
    },
  }
  const unchangedInitialState = { ...initialState }

  const [profile, setProfile] = useState(initialState)

  const handleValidation = (value) => {
    return { isValid: !((value === '') | (value === null)) }
  }

  const handleOnChange = (value, key) => {
    const { isValid } = handleValidation(value)
    const newProfile = { ...profile }
    newProfile[key] = {
      value,
      isValid,
    }
    setProfile(newProfile)
  }

  const handleOnSave = async () => {
    setSaveButtonLoading(true)
    const payload = getProfilePayload(profileData, profile)
    await postData('/api/secure/user/update', payload)
    setProfileData(payload)
    setSaveButtonLoading(false)
    setFormDisabled(true)
    window.location.reload()
  }

  // gender functions and popover:
  const [anchorElGender, setAnchorElGender] = useState()
  const handleGenderClick = (event) => {
    setAnchorElGender(event.currentTarget)
  }
  const handleGenderClose = () => {
    setAnchorElGender(null)
  }
  const genderOpen = Boolean(anchorElGender)
  const genderId = genderOpen ? 'gender-popover' : undefined
  const handleGenderSelect = (selectedGender) => {
    handleOnChange(selectedGender, 'gender')
  }

  // content preferences and popover:
  const [anchorElContentPreferences, setAnchorElContentPreferences] = useState()
  const handleContentPreferencesClick = (event) => {
    setAnchorElContentPreferences(event.currentTarget)
  }
  const handleContentPreferencesClose = () => {
    setAnchorElContentPreferences(null)
  }
  const contentPreferencesOpen = Boolean(anchorElContentPreferences)
  const contentPreferencesId = genderOpen ? 'preference-popover' : undefined
  const handleContentPreferencesSelect = (preferenceItem) => {
    const tempSelectedItems = [...profile.contentPreferences.value]
    if (tempSelectedItems.includes(preferenceItem)) {
      const index = tempSelectedItems.indexOf(preferenceItem)
      if (index > -1) {
        tempSelectedItems?.splice(index, 1)
      }
    } else {
      tempSelectedItems?.push(preferenceItem)
    }
    handleOnChange(tempSelectedItems, 'contentPreferences')
  }

  // content languages functions and popover:
  const [anchorElContentLanguages, setAnchorElContentLanguages] = useState()
  const handleContentLanguagesClick = (event) => {
    setAnchorElContentLanguages(event.currentTarget)
  }
  const handleContentLanguagesClose = () => {
    setAnchorElContentLanguages(null)
  }
  const contentLanguagesOpen = Boolean(anchorElContentLanguages)
  const contentLanguagesId = contentLanguagesOpen ? 'lang-popover' : undefined
  const handleContentLanguagesSelect = (languageItem) => {
    const tempSelectedItems = [...profile.contentLanguages.value]
    if (tempSelectedItems.includes(languageItem)) {
      const index = tempSelectedItems.indexOf(languageItem)
      if (index > -1) {
        tempSelectedItems.splice(index, 1)
      }
    } else {
      tempSelectedItems.push(languageItem)
    }
    handleOnChange(tempSelectedItems, 'contentLanguages')
  }

  ///////////////////

  const selectedGender = profile['gender'].value
  const selectedContentPreferences = profile['contentPreferences']
  const selectedContentLanguages = profile['contentLanguages']

  const languageItems = getLanguageDrawerItems(setupData)
  const preferenceItems = getPreferencesDrawerItems(setupData)

  //  <StyledGenderIcon src={require(`src/assets/${selectedGender === genderOption?.id ? genderOption?.selectedIcon : genderOption?.icon}.png`)} />

  return (
    <>
      <YourProfileContainer disabled={formDisabled}>
        <YourProfileContent>
          <SpaceBetweenFlex>
            <YourProfileTitle>
              {localizedContent?.personal_details || 'PERSONAL DETAILS'}
            </YourProfileTitle>
            {formDisabled && <EditIcon src={pencilIcon} onClick={() => setFormDisabled(false)} />}
          </SpaceBetweenFlex>

          <YourProfileForm disabled={formDisabled}>
            <InputField
              disabled={formDisabled}
              label={localizedContent?.name || 'Name'}
              id="name"
              profile={profile}
              handleOnChange={handleOnChange}
            />

            <StyledAgeGenderContainer>
              <InputField
                disabled={formDisabled}
                label={localizedContent?.age || 'Age'}
                type="number"
                id="age"
                profile={profile}
                handleOnChange={handleOnChange}
              />

              <InputField
                disabled={formDisabled}
                label={localizedContent?.gender || 'Gender'}
                id="gender"
                displayValue={
                  GENDER_LIST?.find((option) => option?.id === profile['gender']?.value)
                    ?.displayValue
                }
                profile={profile}
                handleOnClick={handleGenderClick}
              />
              <Popover
                id={genderId}
                open={genderOpen}
                anchorEl={anchorElGender}
                onClose={handleGenderClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <GenderSelectContainer>
                  {GENDER_LIST?.map((genderOption) => {
                    return (
                      <GenderSelectOptionContainer
                        isSelected={selectedGender === genderOption?.id}
                        key={genderOption?.id}
                        onClick={() => handleGenderSelect(genderOption?.id)}
                      >
                        {genderOption?.displayValue}
                        {selectedGender === genderOption?.id && (
                          <SelectOptionPicture src={greenFilledTick} />
                        )}
                      </GenderSelectOptionContainer>
                    )
                  })}
                </GenderSelectContainer>
              </Popover>
            </StyledAgeGenderContainer>

            <InputField
              disabled={formDisabled}
              setupData={setupData}
              label={localizedContent?.content_preferences || 'Content Preferences'}
              id="contentPreferences"
              profile={profile}
              handleOnClick={handleContentPreferencesClick}
              isClosed={!contentPreferencesOpen}
            />
            <Popover
              id={contentPreferencesId}
              open={contentPreferencesOpen}
              anchorEl={anchorElContentPreferences}
              onClose={handleContentPreferencesClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <ContentPreferencesContainer>
                {preferenceItems?.map((item, index) => {
                  const isSelected = selectedContentPreferences?.value?.includes(item?.id)
                  return (
                    <React.Fragment key={item?.id || index}>
                      <ContentPreferencesOptionContainer
                        isSelected={isSelected}
                        onClick={() => handleContentPreferencesSelect(item?.id)}
                      >
                        <DisplayFlex>
                          <SelectOptionCategoryPicture src={mediaURL + item?.icon} />
                          {item?.title}
                        </DisplayFlex>
                        {isSelected && <SelectOptionPicture src={greenFilledTick} />}
                      </ContentPreferencesOptionContainer>
                      <OptionSeparationLine />
                    </React.Fragment>
                  )
                })}
              </ContentPreferencesContainer>
            </Popover>

            <InputField
              disabled={formDisabled}
              setupData={setupData}
              label={localizedContent?.content_languages || 'Content Languages'}
              id="contentLanguages"
              profile={profile}
              handleOnClick={handleContentLanguagesClick}
              isClosed={!contentLanguagesOpen}
            />
            <Popover
              id={contentLanguagesId}
              open={contentLanguagesOpen}
              anchorEl={anchorElContentLanguages}
              onClose={handleContentLanguagesClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <ContentLanguagesContainer>
                {languageItems?.map((item, index) => {
                  const isSelected = selectedContentLanguages?.value?.includes(item?.id)
                  return (
                    <ContentLanguagesOptionContainer
                      key={item?.id || index}
                      isSelected={isSelected}
                      onClick={() => handleContentLanguagesSelect(item?.id)}
                    >
                      <DisplayFlex>
                        {item?.title}
                        <ContentLanguagesOptionExtraText>
                          {item?.subtitle}
                        </ContentLanguagesOptionExtraText>
                      </DisplayFlex>
                      {isSelected && <SelectOptionPicture src={greenFilledTick} />}
                    </ContentLanguagesOptionContainer>
                  )
                })}
              </ContentLanguagesContainer>
            </Popover>

            <YourProfileActions>
              {!formDisabled && (
                <CancelButton
                  onClick={() => {
                    setProfile(unchangedInitialState)
                    setFormDisabled(true)
                  }}
                >
                  Cancel
                </CancelButton>
              )}

              {formDisabled && (
                <EditButton onClick={() => setFormDisabled(false)}>Edit Profile</EditButton>
              )}

              {!formDisabled && (
                <SaveButton disabled={saveButtonLoading} onClick={handleOnSave}>
                  {!saveButtonLoading ? (
                    localizedContent?.save || 'Save Profile'
                  ) : (
                    <Spinner animation="border" variant="light" size="md" />
                  )}
                </SaveButton>
              )}
            </YourProfileActions>
          </YourProfileForm>
        </YourProfileContent>
      </YourProfileContainer>
    </>
  )
}

export default YourProfile
